const React = require("react");
const AssetsUtil = require("../../core/util/AssetsUtil");
const Link = require("../../core/view/Link");
const {
	genericIcon,
	screenshot,
	accountSetting,
} = require("../../core/util/CopyShared");

// Specifically for the Teacher Accounts support tab.
const getCopy = function(styles, options = {}) {
	return [
		{
			hash: "overview",
			titleLevel: 1,
			contents: (
				<div>
					<div className={styles.rowContainer}>
						<div className={styles.column6}>
							<div className={styles.overviewText}>
								The Accounts page is where you can view and edit account and
								user information. To access your Accounts pages, click ACCOUNTS
								in the top menu. If you have only one class, this will take you
								to your{" "}
								<Link
									to={options.supportLinkURL("accounts", "class-pages")}
									className={styles.link}
								>
									Class
								</Link>
								{
									" page. If you have more than one class, it will take you to your "
								}
								<Link
									to={options.supportLinkURL("accounts", "school-pages")}
									className={styles.link}
								>
									School
								</Link>
								{" page, which includes all of your Classes and Students."}
							</div>
							<br />
							<div className={styles.overviewText}>
								{
									"If you are looking for Progress and Activity, that information is in your "
								}
								<Link
									to={options.supportLinkURL("reports")}
									className={styles.link}
								>
									Reports
								</Link>
								{" pages."}
							</div>
							<div className={styles.videoArea}>
								<iframe
									className={styles.video}
									src="https://www.youtube.com/embed/BIQsJFWzXPc?rel=0"
									allow="autoplay; encrypted-media"
									frameBorder="0"
									allowFullScreen
								/>
							</div>
						</div>
						<div className={styles.column4}>
							<div className={styles.tableOfContents}>
								<div className={styles.tocHeader}>CONTENTS</div>
								<ul className={styles.blueBulletList}>
									<li>
										<Link
											to={options.supportLinkURL(
												"accounts",
												"search-and-navigation"
											)}
											className={styles.link}
										>
											Search and Navigation
										</Link>
									</li>
									<li>
										<Link
											to={options.supportLinkURL("accounts", "settings")}
											className={styles.link}
										>
											Settings
										</Link>
									</li>
									<li>
										<Link
											to={options.supportLinkURL("accounts", "school-pages")}
											className={styles.link}
										>
											School Pages
										</Link>
									</li>
									<li>
										<Link
											to={options.supportLinkURL("accounts", "class-pages")}
											className={styles.link}
										>
											Class Pages
										</Link>
									</li>
									<li>
										<Link
											to={options.supportLinkURL("accounts", "student-pages")}
											className={styles.link}
										>
											Student Pages
										</Link>
									</li>
								</ul>
							</div>
						</div>
					</div>
					<br />
					<div className={styles.redNote}>
						IMPORTANT
						<br />
						System administrators can upload rosters via CSV import in just a
						few simple steps. Note that CSV import will override any manual
						updates that aren't included in the files. To ensure all manual
						changes are kept, system administrators should download the current
						rosters before making changes via CSV import.
					</div>
				</div>
			),
		},
		{
			hash: "search-and-navigation",
			title: "Search and Navigation",
			titleLevel: 1,
			contents: (
				<div>
					There are a few navigation tools that will help you get around the
					Accounts section.
					<div className={styles.level1Screenshot}>
						{screenshot(styles, "accountSearchAndNavigation2", false)}
					</div>
				</div>
			),
		},
		{
			hash: "blue-text",
			title: "Blue Links",
			titleLevel: 4,
			contents: (
				<div>
					<p>
						You can navigate by clicking any of the blue links on an Account
						page. For example, you can click any class or student name to view
						their information page.
					</p>
				</div>
			),
		},
		{
			hash: "breadcrumbs",
			title: "Breadcrumbs",
			titleLevel: 4,
			contents: (
				<div>
					<p>
						Use the "breadcrumbs" near the top-left of any Account page to
						navigate to any item in the breadcrumbs.
					</p>
				</div>
			),
		},
		{
			hash: "search",
			title: "Search",
			titleLevel: 4,
			contents: (
				<div>
					<p>
						You can search for any class or user using the search bar in the
						top-right corner of any Reports page. The search results will
						auto-complete, and will{" "}
						<b>only include classes and users that you have access to</b>.
						Teachers will only be able to view students who are in their
						classes.
					</p>
				</div>
			),
		},
		{
			hash: "settings",
			title: "Settings",
			titleLevel: 1,
			contents: (
				<div>
					Available settings for schools and classes are found near the top of
					the page. School and System Administrators can access these settings
					for all schools and classes, while teachers can only adjust these
					settings for their own classes.
					<br />
					<div className={styles.level1Screenshot}>
						{screenshot(styles, "accountSettings2", false)}
					</div>
					<br />
					{"Click the blue pencil icon "}
					{genericIcon(
						styles,
						AssetsUtil.getScreenshot("blue-edit-pencil"),
						"editPencilOuter",
						"editPencilIcon"
					)}
					{
						" next to any item to edit it. Some settings are only editable by an administrator."
					}
					<br />
					<br />
					<div>
						Schools and Classes have a <b>Lock Scheme</b> and a{" "}
						<b>Problem Set</b>
						{" order. Click the "}
						<div className={styles.learnMoreIcon}>
							<span>?</span>
						</div>
						{
							" icon next to any setting to view more about it and the available options."
						}
					</div>
					<div className={styles.settingsContainer}>
						{accountSetting(styles, "lockSettings")}
						{accountSetting(styles, "fixedSets")}
					</div>
					<div>
						Classes also have a default <b>Grade</b>, <b>BA Level</b>, and{" "}
						<b>Beast Builder Access</b>.{" Click the "}
						<div className={styles.learnMoreIcon}>
							<span>?</span>
						</div>
						{
							" icon next to any setting to view more about it and the available options."
						}
					</div>
					<div className={styles.settingsContainer}>
						{accountSetting(styles, "displayedGrade")}
						{accountSetting(styles, "schoolGrade")}
						{accountSetting(styles, "beast")}
					</div>
					<p>
						The grade is the associated year in school for a class. The BA Level
						is the level in Beast Academy that students will view by default
						when they first play BA Online.
					</p>
					<p>
						<b>
							We recommend that students new to Beast Academy always begin in a
							BA Level that is at least 1 less than their current grade level in
							math.
						</b>
					</p>
				</div>
			),
		},
		{
			hash: "school-pages",
			title: (
				<span className={styles.outer}>
					<span className={styles.baoIcon}>g</span>
					SCHOOL PAGES
				</span>
			),
			titleLevel: 1,
			contents: (
				<div>
					{
						"Click your school name in the navigation breadcrumbs to view your School"
					}
					{" page. Use the "}
					<div className={styles.reportTab}>
						<span className={styles.reportTabIcon}>Q</span>
						Classes
					</div>
					{" and "}
					<div className={styles.reportTab}>
						<span className={styles.reportTabIcon}>G</span>
						Students
					</div>
					{
						" tabs to view all of your classes or students. Click on any Class name"
					}
					{" or Student name to view a "}
					<Link
						to={options.supportLinkURL("accounts", "class-pages")}
						className={styles.link}
					>
						Class
					</Link>
					{" page or "}
					<Link
						to={options.supportLinkURL("accounts", "student-pages")}
						className={styles.link}
					>
						Student
					</Link>
					{" page."}
					<p>
						Administrators also have access to Subscription and Staff views.
					</p>
					{screenshot(styles, "schoolPage2", true)}
					<br />
					{"Click the blue pencil icon "}
					{genericIcon(
						styles,
						AssetsUtil.getScreenshot("blue-edit-pencil"),
						"editPencilOuter",
						"editPencilIcon"
					)}
					{
						" next to any item to edit it. You can edit student names and passwords"
					}
					{" directly from these pages."}
				</div>
			),
		},
		{
			hash: "class-pages",
			title: (
				<div className={styles.outer}>
					<span className={styles.baoIcon}>Q</span>
					CLASS PAGES
				</div>
			),
			titleLevel: 1,
			contents: (
				<div>
					<div>
						Clicking a class name on any account page will take you to its Class
						page, which displays all of its Settings, Teachers, and Students.
						Click any student name to view their{" "}
						<Link
							to={options.supportLinkURL("accounts", "student-pages")}
							className={styles.link}
						>
							Student
						</Link>
						{" page."}
					</div>
					<p style={{lineHeight: 1.5}}>
						{"Click the blue pencil icon "}
						{genericIcon(
							styles,
							AssetsUtil.getScreenshot("blue-edit-pencil"),
							"editPencilOuter",
							"editPencilIcon"
						)}
						{"next to any item to edit it. Administrators can use the "}
						<span className={styles.accountsBlueButton}>ADD</span>
						{" button at the bottom of any table to add an item."}
					</p>
					<div>
						{" Clicking "}
						<span className={styles.blueText}>
							<span className={styles.baoIcon}>h</span>
							GO TO REPORT
						</span>
						{
							" will take you to the Report page for the class in a new browser tab."
						}
					</div>
					<div className={styles.level1Screenshot}>
						{screenshot(styles, "classAccountPage", true)}
					</div>
					<br />
					<div>
						Administrators can{" "}
						<span className={styles.redButton}>DEACTIVATE</span>
						{" a class from this page. "}
						<b>
							Deactivating a class will deactivate all students who are
							associated with this class and any staff who are only associated
							with the class.
						</b>
						{" Deactivated classes can be reactivated later."}
					</div>
				</div>
			),
		},
		{
			hash: "student-pages",
			title: (
				<div className={styles.outer}>
					<span className={styles.baoIcon}>G</span>
					STUDENT PAGES
				</div>
			),
			titleLevel: 1,
			contents: (
				<div>
					Clicking a student name on any account page will take you to their
					Student page, which includes their Username, Display Name, Password,
					and Class.
					<p>
						{"Click the blue pencil icon "}
						{genericIcon(
							styles,
							AssetsUtil.getScreenshot("blue-edit-pencil"),
							"editPencilOuter",
							"editPencilIcon"
						)}
						{"next to any item to edit it."}
					</p>
					<br />
					<div>
						Administrators can{" "}
						<span className={styles.accountsBlueButton}>TRANSFER</span> a
						student into a different class from this page.
					</div>
					<div className={styles.level1Screenshot}>
						{screenshot(styles, "studentPage2", true)}
					</div>
					<br />
					<div>
						You can <span className={styles.redButton}>DEACTIVATE</span> a
						student who is in this class from this page.{" "}
						<b>
							Deactivated students will lose access to BA Online immediately,
							but can be reactivated later and will keep their existing
							progress.
						</b>
					</div>
					<br />
					<div>
						{"Clicking "}
						<span className={styles.blueText}>
							<span className={styles.baoIcon}>h</span>
							GO TO REPORT
						</span>
						{
							" will take you to the Report page for the student in a new browser tab."
						}
					</div>
				</div>
			),
		},
	];
};

module.exports = {
	getCopy,
};
